/**
 * HoldingQueue — /#/contacts/holding
 *
 * Tenant-scoped view of contacts currently in the "holding" lifecycle state.
 * These contacts entered the system without a BANT signal. They have 14 days
 * to gain a qualification signal or they soft-kill, then hard-kill at 30 days.
 *
 * Each row exposes an "Add BANT evidence" inline form. On save, if any letter
 * has content the contact promotes to active and bant_letters_passed is set
 * to the letters that cleared.
 *
 * NOTE: Soft-kill / hard-kill enforcement is handled by a daily job (not yet
 * scheduled — see comment near `// TODO: cron`). This view is the recovery UI.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { useToast } from "@/hooks/use-toast";
import { getCurrentTenant, supabase } from "@/lib/session";
import {
  evaluateBant,
  holdingCountdownTone,
  holdingDaysRemaining,
  HOLDING_HARD_KILL_DAYS,
  HOLDING_SOFT_KILL_DAYS,
  type ContactRow,
} from "@/lib/bant";

const BLACK = "#0B0B0F";
const RED = "#DC2626";

interface HoldingContact extends ContactRow {
  id: number;
  holding_started_at: string;
  lifecycle_state: "holding" | "active" | "soft_killed" | "hard_killed";
}

export default function HoldingQueue() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const tenant = getCurrentTenant();
  const [rows, setRows] = useState<HoldingContact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!tenant) return;
    setError(null);
    const sb = supabase();
    const { data, error } = await sb
      .from("contacts")
      .select("id, first_name, last_name, email, company, title, holding_started_at, lifecycle_state, budget_signal, authority_role, need_evidence, timeline_signal")
      .eq("tenant_id", tenant.id)
      .eq("lifecycle_state", "holding")
      .order("holding_started_at", { ascending: true, nullsFirst: false });
    if (error) {
      setError(error.message);
      return;
    }
    setRows((data as any) ?? []);
  }, [tenant]);

  useEffect(() => { load(); }, [load]);

  const saveBant = useCallback(async (id: number, patch: Partial<ContactRow>) => {
    const sb = supabase();
    const letters = evaluateBant(patch);
    if (letters.length === 0) {
      toast({
        title: "No qualifier signal added",
        description: "Fill in at least one of B, A, N, or T to promote this contact.",
        variant: "destructive",
      });
      return;
    }
    const nowIso = new Date().toISOString();
    const { error } = await sb
      .from("contacts")
      .update({
        budget_signal: patch.budget_signal ?? null,
        authority_role: patch.authority_role ?? null,
        need_evidence: patch.need_evidence ?? null,
        timeline_signal: patch.timeline_signal ?? null,
        bant_letters_passed: letters,
        lifecycle_state: "active",
        last_bant_check_at: nowIso,
      })
      .eq("id", id);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Promoted to active", description: `Cleared CP1 with ${letters.join(" + ")}.` });
    setEditingId(null);
    await load();
  }, [toast, load]);

  return (
    <OperatorShell breadcrumbs={[{ label: "Contacts" }, { label: "Holding Queue" }]} pageTitle="Holding Queue" pageMeta="Contacts · holding queue">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">
            Contacts · holding
          </div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: BLACK }}>
            Holding queue — contacts awaiting qualification
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            These contacts entered the system without enough evidence to clear CP1. They have {HOLDING_SOFT_KILL_DAYS} days
            to gain a qualifier signal or they are removed from cadence eligibility. Permanent removal at {HOLDING_HARD_KILL_DAYS} days.
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 mb-4">
            {error}
          </div>
        )}

        {rows && rows.length === 0 && (
          <div className="rounded-xl border border-neutral-200 bg-white p-10 text-center">
            <div className="text-sm text-neutral-600">
              No contacts in holding. Every active contact has at least one qualifier letter passing.
            </div>
            <button
              onClick={() => navigate("/upload-contacts")}
              className="mt-4 text-xs px-4 py-2 rounded-md font-semibold uppercase tracking-wider text-white"
              style={{ background: BLACK }}
            >
              Upload contacts
            </button>
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
            <div className="px-5 py-3 text-[11px] uppercase tracking-[0.14em] text-neutral-500 border-b border-neutral-100 flex justify-between">
              <span>{rows.length} in holding · oldest first</span>
              <span>Promote with any qualifier letter</span>
            </div>
            <div className="divide-y divide-neutral-100">
              {rows.map((c) => (
                <HoldingRow
                  key={c.id}
                  contact={c}
                  isEditing={editingId === c.id}
                  onEdit={() => setEditingId(c.id)}
                  onCancel={() => setEditingId(null)}
                  onSave={(patch) => saveBant(c.id, patch)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Note for future cron — soft/hard kill enforcement runs daily.
            cron — daily Supabase scheduled function flips
              lifecycle_state=holding → soft_killed at 14d
              lifecycle_state=soft_killed → hard_killed at 30d
        */}
        <div className="mt-6 text-xs text-neutral-500 flex flex-wrap gap-4">
          <button
            onClick={() => navigate("/contacts/all?state=soft_killed")}
            className="underline hover:text-neutral-700"
          >
            View soft-killed (recoverable)
          </button>
          <button
            onClick={() => navigate("/contacts/all?state=hard_killed")}
            className="underline hover:text-neutral-700"
          >
            View hard-killed (archived)
          </button>
        </div>
      </div>
    </OperatorShell>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────

function HoldingRow(props: {
  contact: HoldingContact;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (patch: Partial<ContactRow>) => void;
}) {
  const { contact, isEditing, onEdit, onCancel, onSave } = props;
  const [budget, setBudget] = useState(contact.budget_signal ?? "");
  const [authority, setAuthority] = useState(contact.authority_role ?? "");
  const [need, setNeed] = useState(contact.need_evidence ?? "");
  const [timeline, setTimeline] = useState(contact.timeline_signal ?? "");

  // FENCE-FIX: holding_started_at can be null for contacts imported without
  // a timestamp. holdingDaysRemaining now handles null safely (returns 0).
  const days = holdingDaysRemaining(contact.holding_started_at);
  const tone = holdingCountdownTone(days);
  const TONE_PALETTES = {
    red:   { bg: "#FEE2E2", text: "#991B1B" },
    amber: { bg: "#FEF3C7", text: "#92400E" },
    gray:  { bg: "#EFEFEE", text: "#525258" },
  };
  // Defensive fallback: if tone is ever unexpected, default to gray rather than crashing
  const tonePalette = TONE_PALETTES[tone] ?? TONE_PALETTES.gray;

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-sm font-semibold" style={{ color: BLACK }}>
            {contact.first_name} {contact.last_name}
          </div>
          <div className="text-xs text-neutral-500">
            {contact.title} · {contact.company}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">{contact.email}</div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded tabular-nums"
            style={{ background: tonePalette.bg, color: tonePalette.text }}
            title={`Holding since ${new Date(contact.holding_started_at).toLocaleDateString()}`}
          >
            {days} days remaining
          </span>
          {!isEditing && (
            <button
              onClick={onEdit}
              className="text-[11px] px-3 py-1.5 rounded-md font-semibold uppercase tracking-wider text-white"
              style={{ background: BLACK }}
            >
              Add qualifier evidence
            </button>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3 rounded-lg p-4" style={{ background: "#FAFAF7" }}>
          <BantField label="Budget signal" value={budget} onChange={setBudget} placeholder="approved Q3 budget for tooling" />
          <BantField label="Authority role" value={authority} onChange={setAuthority} placeholder="VP Finance, CFO, Head of Sales" />
          <BantField label="Need evidence" value={need} onChange={setNeed} placeholder="missed forecast last quarter" />
          <BantField label="Timeline signal" value={timeline} onChange={setTimeline} placeholder="decision by Aug 15" />
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button
              onClick={onCancel}
              className="text-[11px] px-3 py-2 rounded-md border border-neutral-200 text-neutral-600"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave({
                budget_signal: budget.trim() || undefined,
                authority_role: authority.trim() || undefined,
                need_evidence: need.trim() || undefined,
                timeline_signal: timeline.trim() || undefined,
              })}
              className="text-[11px] px-4 py-2 rounded-md font-semibold uppercase tracking-wider text-white"
              style={{ background: RED }}
            >
              Promote to active
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BantField(props: { label: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold mb-1">
        {props.label}
      </span>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full text-sm rounded-md border border-neutral-200 bg-white px-3 py-2"
      />
    </label>
  );
}
